import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import styled, { ThemeProvider } from 'styled-components';
import { themeState } from 'styleguide/theme';
import { DisplayXS, TextLG } from 'styleguide/Texts';
import { Card } from 'styleguide/Card';
import { View } from 'styleguide/View';
import { RoutineDetailViewModel } from './RoutineDetailViewModel';
import { SupersetGroupCell } from 'components/ExerciseSummary/SupersetGroupCell';
import { Routine } from 'hevy-shared';
import { sendEvent } from 'utils/events';
import { stores } from 'state/stores';
import { Spacing } from 'styleguide/spacing';
import { DividerLine } from 'styleguide/DividerLine';
import { RoutineOwnerContent } from './components/RoutineOwnerContent';
import { RoutineNotFoundView } from './components/RoutineNotFoundView';
import { RoutineDetailHevyCTACard } from './components/RoutineDetailHevyCTACard';
import { HideOnPhoneWrapper, ShowOnPhoneWrapper } from 'styleguide/Breakpoints';
import { HevyMenuItem, HevyOptionMenu } from 'components/HevyMenu';
import router from 'next/router';
import { FlexRow } from 'styleguide/Row';

interface RoutineDetailScreenProps {
  routine?: Routine;
  isAuthenticated: boolean;
}

export const RoutineDetailScreen = ({ routine, isAuthenticated }: RoutineDetailScreenProps) => {
  const [vm] = useState(new RoutineDetailViewModel({ routine, isAuthenticated }));

  useEffect(() => {
    setTimeout(() => {
      /**
       * Without setTimeout this event is fired before
       * amplitude has been initialized.
       */
      sendEvent('routineDetail_screenview', {
        authenticated: stores.auth.isAuthenticated,
      });
    });
  }, []);

  return <RoutineDetailScreenInner vm={vm} />;
};

const ScreenContainer = styled(View)`
  flex: 1;
  overflow-y: auto;
  justify-content: center;
  align-items: center;
`;

const Container = styled(View)`
  flex: 1;
  width: 100%;
  max-width: 1048px;
  min-height: 90vh;
`;

const Content = styled(View)`
  flex-direction: row;
  justify-content: center;
  gap: ${Spacing.lg}px;
  margin: ${Spacing.lg}px 0px;
  padding: 0px ${Spacing.md}px ${Spacing.lg}px;
`;

const LeftContainer = styled(View)`
  flex: 684;
  @media (max-width: 820px) {
    min-width: 80vw;
  }
  max-width: 684px;
  gap: ${Spacing.md}px;
`;

const RoutineHeaderContainer = styled(View)`
  padding: ${Spacing.lg}px;
  gap: ${Spacing.md}px;
`;

const ExercisesContainer = styled(View)`
  padding: ${Spacing.md}px;
`;

const RightContainer = styled(HideOnPhoneWrapper)`
  display: flex;
  flex-direction: column;
  flex: 330;
  max-width: 330px;
  gap: ${Spacing.md}px;
`;

interface RoutineDetailScreenInnerProps {
  vm: RoutineDetailViewModel;
}

const RoutineDetailScreenInner = observer(({ vm }: RoutineDetailScreenInnerProps) => {
  return (
    <ThemeProvider theme={themeState.current}>
      <ScreenContainer>
        <Container>
          {vm.isError ? (
            <RoutineNotFoundView />
          ) : (
            <Content>
              <LeftContainer>
                <Card>
                  <RoutineHeaderContainer>
                    <FlexRow style={{ justifyContent: 'space-between' }}>
                      <DisplayXS>{vm.title}</DisplayXS>
                      {vm.isUsersRoutine && (
                        <HevyOptionMenu>
                          <HevyMenuItem
                            title="Copy Routine Link"
                            onClick={vm.onCopyUrl}
                            iconType="copyGrey"
                          />
                          <HevyMenuItem
                            title="Edit Routine"
                            onClick={() => {
                              router.push(`/edit-routine/${vm.routineId}`);
                            }}
                            iconType="editGrey"
                          />
                          <HevyMenuItem
                            title="Delete Routine"
                            onClick={vm.onDeleteRoutine}
                            iconType="xSmallGrey"
                          />
                        </HevyOptionMenu>
                      )}
                    </FlexRow>
                    <ShowOnPhoneWrapper>
                      <RoutineOwnerContent vm={vm}></RoutineOwnerContent>
                    </ShowOnPhoneWrapper>
                  </RoutineHeaderContainer>

                  <DividerLine />

                  <ExercisesContainer>
                    <TextLG style={{ paddingLeft: Spacing.sm, paddingBottom: Spacing.sm }}>
                      Exercises
                    </TextLG>
                    {vm.supersetGroups.map((supersetGroup, index) => {
                      return (
                        <SupersetGroupCell
                          key={`${supersetGroup.supersetId || supersetGroup.exercises[0]?.title}`}
                          supersetId={supersetGroup.supersetId}
                          exercises={supersetGroup.exercises}
                          showDivider={index !== vm.supersetGroups.length - 1}
                        />
                      );
                    })}
                  </ExercisesContainer>
                </Card>
                <ShowOnPhoneWrapper>
                  {!vm.isAuthenticated && <RoutineDetailHevyCTACard />}
                </ShowOnPhoneWrapper>
              </LeftContainer>
              <RightContainer>
                <Card style={{ padding: Spacing.lg }}>
                  <RoutineOwnerContent vm={vm}></RoutineOwnerContent>
                </Card>
                {!vm.isAuthenticated && <RoutineDetailHevyCTACard />}
              </RightContainer>
            </Content>
          )}
        </Container>
      </ScreenContainer>
    </ThemeProvider>
  );
});
